import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Divider from "@mui/material/Divider";
import { TabView, TabPanel } from "primereact/tabview";
import { Sidebar } from "primereact/sidebar";
import notificationScreens from "../../services/profileScreen/notificationScreens";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import PatientSearch from "../Shared/patientSearch";
import search from "../../assets/images/search.svg";

const PAGE_SIZE = 5;

const Email = ({ show, setShow }) => {
  const toastRef = useRef(null);
  
  const [sentMail, setSentMail] = useState([]);
  const [getMail, setGetMail] = useState([]);
  const [liveTime, setLiveTime] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [patientSearch, setPatientSearch] = useState(false);
  const [emailData, setEmailData] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPagesent, setCurrentPagesent] = useState(0);

  const userObj = JSON.parse(localStorage.getItem("loginObj") || "{}");
  const user = userObj.user || {};
  const testParam = user.code;
  const emailId = user.email;
  const toName = `${user.salutation || ""} ${user.firstName || ""} ${user.lastName || ""}`.trim();

  const togglePatientSearchbar = useCallback(() => {
    setPatientSearch(prev => !prev);
  }, []);

  const handleSelectedData = useCallback((selectedData) => {
    setEmailData(selectedData);
  }, []);

  const showError = useCallback((message) => {
    toastRef.current?.show({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });
  }, []);

  const showSuccess = useCallback((message) => {
    toastRef.current?.show({
      severity: 'success', 
      summary: 'Success', 
      detail: message, 
      life: 3000
    });
  }, []);

  const emailSentAPI = useCallback(async () => {
    try {
      const res = await notificationScreens.emailSentAPI(emailId);
      if (res?.data?.successList) {
        setSentMail(res.data.successList);
      }
    } catch (error) {
      console.error("Error fetching sent emails:", error);
    }
  }, [emailId]);

  const emailGetAPIs = useCallback(async () => {
    try {
      const res = await notificationScreens.getEmailMailbycode(testParam);
      if (res?.data?.successList) {
        setGetMail(res.data.successList);
      }
    } catch (error) {
      console.error("Error fetching received emails:", error);
    }
  }, [testParam]);

  const getCurrentTime = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const date = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${date} ${hours}:${minutes}:${seconds}`;
  }, []);

  const sendMail = useCallback(async () => {
    if (!emailData?.code) {
      showError("Please select a recipient");
      return;
    }

    if (!subject?.trim()) {
      showError("Subject is required");
      return;
    }

    if (!body?.trim()) {
      showError("Message body is required");
      return;
    }

    const payload = {
      createdBy: emailId,
      createdDate: liveTime,
      fromDate: liveTime,
      modifiedBy: emailId,
      modifiedDate: liveTime,
      status: "1",
      subject: subject.trim(),
      content: body.trim(),
      toCode: emailData.code,
      toName: emailData.name,
      patientName: emailData.name,
      fromCode: emailId,
      fromName: toName,
      trigeredUsertype: "P",
    };

    try {
      const res = await notificationScreens.sendmail(payload);
      if (res?.data?.responseCode === '200') {
        setSubject("");
        setBody("");
        setShow(false);
        setEmailData(null);
        showSuccess("Mail Sent Successfully");
        emailSentAPI();
      } else {
        showError(res?.data?.message || "Failed to send email");
      }
    } catch (error) {
      let errorMessage = "Error sending mail";
      if (error.response?.data) {
        const errorData = error.response.data;
        errorMessage = errorData.message || errorMessage;
        if (errorData.details?.[1]) {
          errorMessage += `: ${errorData.details[1]}`;
        }
      }
      showError(errorMessage);
    }
  }, [emailData, subject, body, emailId, liveTime, toName, setShow, showError, showSuccess, emailSentAPI]);

  const onPageChange = useCallback((event) => {
    setCurrentPage(event.page);
  }, []);

  const onPageChangeNew = useCallback((event) => {
    setCurrentPagesent(event.page);
  }, []);

  const getPaginatedData = useCallback((data, currentPageIndex) => {
    const startIndex = currentPageIndex * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return data.slice(startIndex, endIndex);
  }, []);

  const getMailData = getPaginatedData(getMail, currentPage);
  const sentMailData = getPaginatedData(sentMail, currentPagesent);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    return dateString.substring(0, 10);
  }, []);

  useEffect(() => {
    let intervalId;

    const updateTime = () => {
      setLiveTime(getCurrentTime());
    };

    updateTime();
    intervalId = setInterval(updateTime, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [getCurrentTime]);

  useEffect(() => {
    if (show) {
      emailSentAPI();
      emailGetAPIs();
    }
  }, [show, emailSentAPI, emailGetAPIs]);

  const handleSearchIconKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      togglePatientSearchbar();
    }
  };

  const renderEmailItem = useCallback((data, index) => (
    <div
      className="w-80 h-28 rounded-xl bg-white border border-gray-300 mb-4 overflow-hidden"
      key={data.id || index}
    >
      <div className="flex flex-row items-center h-full">
        <div className="flex flex-col ml-4 w-full">
          <h1 className="font-bold mb-3 mt-7 truncate">
            {data.toName || "Unknown"}
          </h1>
          <p className="text-sm whitespace-normal overflow-ellipsis overflow-hidden line-clamp-2">
            {data.content || "No content"}
          </p>
          <Divider style={{ marginTop: "10px" }} />
          <div className="text-sm mt-4 pi pi-calendar mb-10 flex items-center gap-2">
            {formatDate(data.createdDate)}
          </div>
        </div>
      </div>
    </div>
  ), [formatDate]);

  const renderReceivedTab = useCallback(() => (
    <>
      {getMailData.length > 0 ? (
        <>
          {getMailData.map(renderEmailItem)}
          <Paginator
            first={currentPage * PAGE_SIZE}
            rows={PAGE_SIZE}
            totalRecords={getMail.length}
            onPageChange={onPageChange}
            className="mt-4"
          />
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No received emails
        </div>
      )}
    </>
  ), [getMailData, getMail.length, currentPage, onPageChange, renderEmailItem]);

  const renderSentTab = useCallback(() => (
    <>
      {sentMailData.length > 0 ? (
        <>
          {sentMailData.map(renderEmailItem)}
          <Paginator
            first={currentPagesent * PAGE_SIZE}
            rows={PAGE_SIZE}
            totalRecords={sentMail.length}
            onPageChange={onPageChangeNew}
            className="mt-4"
          />
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No sent emails
        </div>
      )}
    </>
  ), [sentMailData, sentMail.length, currentPagesent, onPageChangeNew, renderEmailItem]);

  const renderNewMailTab = useCallback(() => {
    const fromId = "email-from-input";
    const toId = "email-to-input";
    const subjectId = "email-subject-input";
    const messageId = "email-message-input";

    return (
      <>
        <div className="flex flex-col">
          <label htmlFor={fromId} className="block text-sm font-medium text-black mb-4">
            From
          </label>
          <InputText
            id={fromId}
            value={toName}
            disabled
            placeholder="From...."
            className="border rounded border-solid border-[#E2E2E2] box-border w-full pl-3 pr-10"
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor={toId} className="block text-sm font-medium text-black mb-4 mt-8">
            To
          </label>
          <div className="relative w-full">
            <InputText
              id={toId}
              placeholder="To...."
              value={emailData?.name || ""}
              className="border rounded border-solid border-[#E2E2E2] box-border w-full pl-3 pr-10 cursor-pointer"
              onClick={togglePatientSearchbar}
              readOnly
            />
          <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 cursor-pointer bg-transparent border-none p-0"
              onClick={togglePatientSearchbar}
              onKeyDown={handleSearchIconKeyDown}
              aria-label="Search patient"
            >
              <img
                src={search}
                alt=""
                className="w-4 h-4"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div className="flex flex-col">
          <label htmlFor={subjectId} className="block text-sm font-medium text-black mb-4 mt-8">
            Subject
          </label>
          <InputText
            id={subjectId}
            value={subject}
            placeholder="Subject...."
            onChange={(e) => setSubject(e.target.value)}
            className="border rounded border-solid border-[#E2E2E2] box-border w-full pl-3 pr-10"
            maxLength={200}
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor={messageId} className="block text-sm font-medium text-black mb-4 mt-8">
            Message
          </label>
          <InputTextarea
            id={messageId}
            value={body}
            placeholder="Type Here ......"
            rows={10}
            cols={30}
            onChange={(e) => setBody(e.target.value)}
            className="border rounded border-solid border-[#E2E2E2] box-border w-full pl-3 pr-10"
            maxLength={5000}
          />
        </div>
        <button
          className="w-full h-10 border border-[#004172] bg-[#004172] text-white font-medium rounded-md mt-6 hover:bg-[#00315a] transition-colors"
          onClick={sendMail}
          disabled={!emailData?.code || !subject.trim() || !body.trim()}
        >
          Send Mail
        </button>
      </>
    );
  }, [toName, emailData, subject, body, togglePatientSearchbar, sendMail]);

  return (
    <>
      <Toast ref={toastRef} />
      <Sidebar
        visible={show}
        position="right"
        onHide={() => setShow(false)}
        className="w-[500px]"
      >
        <h2 className="font-bold text-2xl leading-6 tracking-normal text-left text-[#262626] mb-6">
          Emails
        </h2>

        <TabView className="mt-4">
          <TabPanel header="Received">
            {renderReceivedTab()}
          </TabPanel>
          <TabPanel header="Sent">
            {renderSentTab()}
          </TabPanel>
          <TabPanel header="New Mail">
            {renderNewMailTab()}
          </TabPanel>
        </TabView>
      </Sidebar>
      <PatientSearch
        showbar={patientSearch}
        setshowBar={togglePatientSearchbar}
        onSelect={handleSelectedData}
      />
    </>
  );
};

Email.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
};

export default Email;